/**
 * abat CI/CD GUI - application shell
 *
 * Scoped down for the CI/CD product.
 * No message monitor, no AI chat, no builder: the focus is on spaces,
 * transport, and CI/CD governance (setup, monitor, release).
 *
 * Routing: state based, synced to the URL via pushState/popstate.
 * No deep link to individual iFlows anymore: the product has no
 * message view.
 */
import { useState, useEffect, useCallback, useRef, useMemo, Component } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { useTranslation } from "react-i18next";

// Sets up i18next and dispatches the iio-lang-change event on every switch, so
// any other module with its own catalog could pick it up (the transport wizard
// under pages/transport-v2 does not currently listen; it only shares the same
// iio-lang localStorage key at initial load). Without this import, i18next is
// never initialized, and every page shows translation keys instead of text.
import i18n from "./i18n";

import { FONT } from "./theme/tokens";
import { buildMuiTheme } from "./theme/muiTheme";
import { buildCSS } from "./theme/cssVariables";
import { ThemeCtx } from "./theme/ThemeContext";
import { api, v, getToken, setToken } from "./utils/helpers";

import LoginPage from "./pages/LoginPage";
import SpacesHome from "./pages/SpacesHome";
import SpaceDetail from "./pages/SpaceDetail";
import InviteAccept from "./pages/InviteAccept";
import SettingsModal from "./pages/SettingsModal";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import TransportV2Page from "./pages/transport-v2/TransportV2Page";
import BenutzerAnlegen from "./pages/BenutzerAnlegen";
import BenutzerVerwalten from "./pages/BenutzerVerwalten";
import CicdSetup from "./pages/CicdSetup";
import CicdPipeline from "./pages/CicdPipeline";
import ReleaseFreigabe from "./pages/ReleaseFreigabe";

// TanStack Query stays in place as infrastructure; the newer pages can adopt it
// gradually. SSE is gone (no monitor anymore, no push events).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Catches render errors so a readable message shows up instead of a blank
// screen.
// Class component: hooks do not work here, hence i18n.t instead of useTranslation.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("React Error Boundary caught:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "var(--iio-bg-0)", color: "var(--iio-text-1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
          <div style={{ textAlign: "center", maxWidth: 520, padding: 40 }}>
            <h2 style={{ marginBottom: 8 }}>{i18n.t("navigation:fehler.titel")}</h2>
            <p style={{ color: "var(--iio-text-2)", marginBottom: 16 }}>{this.state.error?.message || i18n.t("navigation:fehler.unbekannt")}</p>
            <pre style={{ textAlign: "left", background: "var(--iio-bg-2)", padding: 16, borderRadius: 8, fontSize: 12, overflow: "auto", maxHeight: 200, color: "var(--iio-critical)", fontFamily: "inherit" }}>{this.state.error?.stack?.split("\n").slice(0, 8).join("\n")}</pre>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}
              style={{ marginTop: 16, padding: "10px 24px", borderRadius: 8, border: "none", background: "var(--iio-brand-ink)", color: "var(--iio-hero-text)", fontSize: 14, cursor: "pointer" }}
            >
              {i18n.t("navigation:fehler.zurStartseite")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { t } = useTranslation("navigation");

  // Base state
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState("spaces");
  const [spaces, setSpaces] = useState([]);
  const [activeSpace, setActiveSpace] = useState(null);
  const [activeInst, setActiveInst] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [branding, setBranding] = useState({ title: "abat CI/CD GUI", subtitle: "Transport & CI/CD für die SAP Integration Suite", logoUrl: "", faviconUrl: "" });
  // CI/CD binding per space (loaded once, centrally, and passed down to
  // SpacesHome/SpaceDetail and the sidebar).
  const [cicdStatus, setCicdStatus] = useState({ tokenSet: false, spaces: {} });
  // Instances per space for the sidebar tree (lazily loaded on expand).
  const [treeExpSpaces, setTreeExpSpaces] = useState({});
  const [hoverInstances, setHoverInstances] = useState({});

  const inviteToken = window.location.pathname.startsWith("/invite/")
    ? window.location.pathname.split("/invite/")[1]
    : null;

  // URL routing
  const pageRef = useRef(page); pageRef.current = page;

  const buildUrl = (pg, space, inst) => {
    if (pg === "spaces") return "/";
    if (pg === "space" && space) return `/space/${space.id}`;
    if (pg === "space" && inst) return `/space/${inst.spaceId || activeSpace?.id || "_"}`;
    if (pg === "transport-v2") return "/transport-v2";
    if (pg === "benutzer-anlegen") return "/benutzer-anlegen";
    if (pg === "benutzer-verwalten") return "/benutzer-verwalten";
    if (pg === "cicd-setup") return "/cicd-setup";
    if (pg === "cicd-pipeline") return "/cicd-pipeline";
    if (pg === "transport-freigabe") return "/transport-freigabe";
    if (pg === "invite" && inviteToken) return `/invite/${inviteToken}`;
    return "/";
  };

  const navigate = (pg, space, inst) => {
    setPage(pg);
    if (space !== undefined) setActiveSpace(space);
    if (inst !== undefined) setActiveInst(inst);
    const url = buildUrl(pg, space || activeSpace, inst || activeInst);
    window.history.pushState({ page: pg }, document.title, url);
    setMobileNavOpen(false);
  };

  // Parse the URL on initial load.
  useEffect(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts[0] === "transport-v2") { setPage("transport-v2"); return; }
    if (parts[0] === "benutzer-anlegen") { setPage("benutzer-anlegen"); return; }
    if (parts[0] === "benutzer-verwalten") { setPage("benutzer-verwalten"); return; }
    if (parts[0] === "cicd-setup") { setPage("cicd-setup"); return; }
    if (parts[0] === "cicd-pipeline") { setPage("cicd-pipeline"); return; }
    if (parts[0] === "transport-freigabe") { setPage("transport-freigabe"); return; }
    if (parts[0] === "invite" && parts[1]) { setPage("invite"); return; }
    if (parts[0] === "space" && parts[1]) {
      setPage("space");
      window.__pendingRoute = { spaceId: parts[1] };
    }
  }, []);

  // Branding and favicon
  useEffect(() => { fetch("/api/branding").then(r => r.json()).then(setBranding).catch(() => {}); }, []);
  useEffect(() => {
    let l = document.querySelector("link[rel~='icon']");
    if (branding.faviconUrl) {
      if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
      l.href = branding.faviconUrl;
    } else if (l) {
      // Set an empty icon instead of removing the element, otherwise browsers keep the
      // old tab icon from cache.
      l.href = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
    }
    if (branding.title) document.title = branding.title;
  }, [branding]);

  // Authentication
  useEffect(() => {
    // Deliberately not named t, which would shadow the translation function.
    const gespeicherterToken = getToken();
    if (!gespeicherterToken) { setChecking(false); return; }
    api("/api/auth/me").then(setUser).catch(() => setToken(null)).finally(() => setChecking(false));
  }, []);

  // CI/CD binding per space (loaded once, centrally)
  const loadCicdStatus = useCallback(() => {
    if (!user) return;
    api("/api/cicd/spaces-status")
      .then(r => setCicdStatus({ tokenSet: !!r?.tokenSet, spaces: r?.spaces || {} }))
      .catch(() => setCicdStatus({ tokenSet: false, spaces: {} }));
  }, [user]);
  useEffect(() => { loadCicdStatus(); }, [loadCicdStatus]);

  // Load spaces
  const loadSpaces = useCallback(async () => {
    let sp;
    try { sp = await api("/api/spaces"); setSpaces(sp); }
    catch (e) { console.error("[Route] /api/spaces failed:", e); return; }
    if (!window.__pendingRoute) return;
    const { spaceId } = window.__pendingRoute;
    delete window.__pendingRoute;
    if (!spaceId) return;
    const space = sp.find(s => s.id === spaceId);
    if (!space) { console.warn("[Route] Space", spaceId, "nicht gefunden"); setPage("spaces"); return; }
    setActiveSpace(space);
    setPage("space");
  }, []);
  useEffect(() => {
    if (user && !user.mustChangePassword) {
      loadSpaces();
      if (inviteToken) setPage("invite");
    }
  }, [user, loadSpaces, inviteToken]);

  // Back/forward navigation
  useEffect(() => {
    const onPop = () => {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const topLevel = ["transport-v2", "benutzer-anlegen", "benutzer-verwalten", "cicd-setup", "cicd-pipeline", "transport-freigabe"];
      if (topLevel.includes(parts[0])) { setPage(parts[0]); return; }
      if (parts[0] === "space" && parts[1]) {
        const existing = spaces.find(s => s.id === parts[1]);
        if (existing) { setActiveSpace(existing); setActiveInst(null); setPage("space"); }
        else { window.__pendingRoute = { spaceId: parts[1] }; setPage("space"); loadSpaces(); }
        return;
      }
      setPage("spaces"); setActiveSpace(null); setActiveInst(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [spaces, loadSpaces]);

  const logout = () => {
    setToken(null);
    setUser(null);
    setSpaces([]);
    setPage("spaces");
    setActiveSpace(null);
    setActiveInst(null);
    setMobileNavOpen(false);
    window.history.pushState({}, "", "/");
  };

  // Load instances for a space when it gets expanded in the sidebar.
  const loadInstancesForSpace = async (spaceId) => {
    if (hoverInstances[spaceId]) return;
    try {
      const insts = await api(`/api/spaces/${spaceId}/instances`);
      setHoverInstances(p => ({ ...p, [spaceId]: insts }));
    } catch {}
  };

  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem("cpi_theme") || "dark");
  const toggleTheme = () => {
    const naechstesTheme = theme === "dark" ? "light" : "dark";
    const apply = () => { setTheme(naechstesTheme); localStorage.setItem("cpi_theme", naechstesTheme); };
    if (document.startViewTransition) document.startViewTransition(apply);
    else {
      document.documentElement.classList.add("theme-fade");
      apply();
      setTimeout(() => document.documentElement.classList.remove("theme-fade"), 380);
    }
  };
  // MUI portals (dialogs) render outside the data-theme div, so the attribute
  // also needs to be set on <html> for them to inherit the CSS variables.
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); }, [theme]);
  const muiTheme = useMemo(() => buildMuiTheme(theme), [theme]);

  // Sidebar fine-tuning
  const [sbRail, setSbRail] = useState(() => { try { return localStorage.getItem("iio_sb_rail") === "1"; } catch { return false; } });
  const toggleSbRail = () => setSbRail(r => { const n = !r; try { localStorage.setItem("iio_sb_rail", n ? "1" : "0"); } catch {} return n; });
  const [sbGrpOpen, setSbGrpOpen] = useState(true);
  const [sbUserMenu, setSbUserMenu] = useState(false);
  const sbScrollRef = useRef(null);
  const sbMarkerRef = useRef(null);
  const [sbFades, setSbFades] = useState({ top: false, bot: false });
  const updateSbFades = useCallback(() => {
    const sc = sbScrollRef.current; if (!sc) return;
    setSbFades(p => {
      const top = sc.scrollTop > 4;
      const bot = sc.scrollTop < sc.scrollHeight - sc.clientHeight - 4;
      return (p.top === top && p.bot === bot) ? p : { top, bot };
    });
  }, []);
  const repositionSbMarker = useCallback(() => {
    const sc = sbScrollRef.current, mk = sbMarkerRef.current;
    if (!sc || !mk) return;
    const el = sc.querySelector(".iio-sb2-item.is-active");
    if (!el || el.getBoundingClientRect().height < 4) { mk.style.opacity = "0"; return; }
    const sr = sc.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const h = Math.max(16, Math.min(34, Math.round(er.height) - 8));
    mk.style.height = h + "px";
    mk.style.transform = "translateY(" + ((er.top - sr.top) + sc.scrollTop + (er.height - h) / 2) + "px)";
    mk.style.opacity = "1";
  }, []);
  useEffect(() => {
    const raf = requestAnimationFrame(() => { repositionSbMarker(); updateSbFades(); });
    const nachlauf = setTimeout(() => { repositionSbMarker(); updateSbFades(); }, 360);
    return () => { cancelAnimationFrame(raf); clearTimeout(nachlauf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeInst, sbGrpOpen, treeExpSpaces, sbRail, spaces, hoverInstances]);
  useEffect(() => {
    if (!sbUserMenu) return;
    const h = () => setSbUserMenu(false);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [sbUserMenu]);

  // Global search (spaces and instances only)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const searchTimeout = useRef(null);
  const runSearch = async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    try {
      const results = { instances: [], spaces: [] };
      const needle = q.toLowerCase();
      for (const sp of spaces) {
        if (sp.name?.toLowerCase().includes(needle)) results.spaces.push(sp);
        try {
          const insts = await api(`/api/spaces/${sp.id}/instances`);
          for (const inst of insts) {
            if (inst.name?.toLowerCase().includes(needle)) {
              results.instances.push({ ...inst, spaceName: sp.name, spaceId: sp.id });
            }
          }
        } catch {}
      }
      setSearchResults(results);
    } catch {}
  };
  const handleSearch = (val) => {
    setSearchQuery(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => runSearch(val), 500);
  };

  const CSS = buildCSS();

  // Auth gates
  if (checking) return (
    <QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/>
      <div data-theme={theme}><style>{CSS}</style>
        <div style={{ minHeight: "100vh", background: v("bg"), display: "flex", alignItems: "center", justifyContent: "center", color: v("text-3") }}>{t("gemeinsam:zustand.laedt")}</div>
      </div>
    </ThemeProvider></ErrorBoundary></QueryClientProvider>
  );
  if (!user) return (
    <QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/>
      <ThemeCtx.Provider value={{ theme, toggle: toggleTheme }}>
        <div data-theme={theme}><style>{CSS}</style>
          <LoginPage onLogin={setUser} inviteToken={inviteToken}/>
        </div>
      </ThemeCtx.Provider>
    </ThemeProvider></ErrorBoundary></QueryClientProvider>
  );
  if (user.mustChangePassword) return (
    <QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/>
      <ThemeCtx.Provider value={{ theme, toggle: toggleTheme }}>
        <div data-theme={theme}><style>{CSS}</style>
          <ForcePasswordChange onDone={() => setUser({ ...user, mustChangePassword: false })}/>
        </div>
      </ThemeCtx.Provider>
    </ThemeProvider></ErrorBoundary></QueryClientProvider>
  );

  // Navigation items for the sidebar
  const isAdmin = user?.role === "admin";
  const workNavItems = [
    {
      key: "transport-v2", label: t("eintrag.transport"), active: page === "transport-v2",
      onClick: () => navigate("transport-v2", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9h16M4 15h16"/><path d="m16 6 4 3-4 3M8 12l-4 3 4 3"/></svg>,
    },
  ];
  const adminNavItems = [
    {
      key: "cicd-setup", label: t("eintrag.cicdSetup"), active: page === "cicd-setup",
      onClick: () => navigate("cicd-setup", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.9 4.9l2.9 2.9M16.2 16.2l2.9 2.9M2 12h4M18 12h4M4.9 19.1l2.9-2.9M16.2 7.8l2.9-2.9"/><circle cx="12" cy="12" r="3"/></svg>,
    },
    {
      key: "cicd-pipeline", label: t("eintrag.cicdMonitor"), active: page === "cicd-pipeline",
      onClick: () => navigate("cicd-pipeline", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
    },
    {
      key: "transport-freigabe", label: t("eintrag.transportFreigabe"), active: page === "transport-freigabe",
      onClick: () => navigate("transport-freigabe", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg>,
    },
    {
      key: "benutzer-anlegen", label: t("eintrag.benutzerAnlegen"), active: page === "benutzer-anlegen",
      onClick: () => navigate("benutzer-anlegen", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M17 11h4M19 9v4"/></svg>,
    },
    {
      key: "benutzer-verwalten", label: t("eintrag.benutzerVerwalten"), active: page === "benutzer-verwalten",
      onClick: () => navigate("benutzer-verwalten", null, null),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3.4"/><path d="M2.5 21a6.5 6.5 0 0 1 13 0"/><circle cx="18.5" cy="9" r="2.4"/><path d="M15.5 21a4.2 4.2 0 0 1 6.5-3.5"/></svg>,
    },
  ];

  return (
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <ThemeProvider theme={muiTheme}><CssBaseline/>
    <ThemeCtx.Provider value={{ theme, toggle: toggleTheme }}>
    <div data-theme={theme} className="iio">
      <style>{CSS}</style>
      <div className="iio-bgfx" aria-hidden="true"/>

      {/* Mobile overlay backdrop */}
      <div className={"iio-mobile-backdrop" + (mobileNavOpen ? " is-open" : "")} onClick={() => setMobileNavOpen(false)}/>

      <div className={"iio-app" + (sbRail ? " is-rail" : "")}>

        {/* Sidebar: navigation scrollable, user fixed.
            The brand sits in the topbar, see .iio-topbar-lead. */}
        <aside className={"iio-sidebar" + (mobileNavOpen ? " is-open" : "")}>

          {/* Zone 2: navigation.
              The fade edges lie as an overlay ON the wrapper, not inside the
              scroller; only that way do they end exactly on the divider line
              towards the user zone. */}
          <div className="iio-sb2-navwrap">
          <nav className="iio-sb2-scroll" ref={sbScrollRef} onScroll={updateSbFades}>
            <span className="iio-sb2-marker" ref={sbMarkerRef} style={{ transform: "translateY(0)", opacity: 0 }}/>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.arbeitsbereich")}</div>

              {/* Spaces group with expandable tree */}
              <div className={"iio-sb2-grp" + (sbGrpOpen ? " open" : "")}>
                <button type="button"
                  className={"iio-sb2-item" + ((page === "spaces" || page === "space") ? " is-active" : "")}
                  title={t("eintrag.spaces")}
                  onClick={() => { setSbGrpOpen(true); navigate("spaces", null, null); loadSpaces(); setMobileNavOpen(false); }}>
                  <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"><path d="M4 8.5 12 4l8 4.5-8 4.5-8-4.5z"/><path d="M4 13l8 4.5 8-4.5" opacity=".55"/><path d="M4 17.5 12 22l8-4.5" opacity=".3"/></svg></span>
                  <span className="lbl">{t("eintrag.spaces")}</span>
                  {spaces.length > 0 && <span className="cnt">{spaces.length}</span>}
                  <span className="iio-sb2-grpchev" role="button" tabIndex={0} aria-label={sbGrpOpen ? t("baum.spacesZuklappen") : t("baum.spacesAufklappen")}
                    onClick={(e) => { e.stopPropagation(); setSbGrpOpen(o => !o); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setSbGrpOpen(o => !o); } }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </span>
                </button>
                <div className="iio-sb2-grpbody"><div>
                  <div className="iio-sb2-tree">
                    {spaces.map((sp, spIdx) => {
                      const isSpaceOpen = !!treeExpSpaces[sp.id];
                      const insts = hoverInstances[sp.id] || [];
                      const cicd = cicdStatus?.spaces?.[sp.id];
                      const cicdReady = !!cicd?.pipelineReady;
                      const cicdConfigured = !!cicd?.configured;
                      // The value doubles as the decision whether the marker shows at
                      // all, so it stays null rather than an empty string.
                      const cicdTitle = cicdReady
                        ? t("cicd.bereit")
                        : cicdConfigured
                          ? t("cicd.ohneGithub")
                          : null;
                      const glyphs = [
                        <svg key="g0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M4 12h16M12 4v16" opacity=".5"/></svg>,
                        <svg key="g1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3l8 5v8l-8 5-8-5V8l8-5z"/><path d="M12 21V11M4 8l8 3 8-3" opacity=".5"/></svg>,
                        <svg key="g2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v8M8 12h8" opacity=".55"/></svg>,
                        <svg key="g3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 5h14v14H5z" transform="rotate(45 12 12)"/><path d="M12 8.5v7" opacity=".5"/></svg>,
                      ];
                      return (
                        <div key={sp.id} className={"iio-sb2-sp" + (isSpaceOpen ? " open" : "")}>
                          <button type="button" className="iio-sb2-sprow" title={t("baum.spaceOeffnen", { name: sp.name })}
                            onClick={() => { navigate("space", sp, null); }}>
                            <span className="iio-sb2-spchev" role="button" tabIndex={0} aria-label={isSpaceOpen ? t("baum.instanzenZuklappen") : t("baum.instanzenAufklappen")}
                              onClick={(e) => {
                                e.stopPropagation();
                                const next = !isSpaceOpen;
                                setTreeExpSpaces(p => ({ ...p, [sp.id]: next }));
                                if (next) loadInstancesForSpace(sp.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault(); e.stopPropagation();
                                  const next = !isSpaceOpen;
                                  setTreeExpSpaces(p => ({ ...p, [sp.id]: next }));
                                  if (next) loadInstancesForSpace(sp.id);
                                }
                              }}>
                              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                            </span>
                            <span className="glyph">{glyphs[spIdx % glyphs.length]}</span>
                            <span className="lbl">{sp.name}</span>
                            {/* Word instead of pictogram: the wave icon said nothing, the
                                word names the fact. The title still tells ready apart
                                from "GitHub missing". */}
                            {cicdTitle && (
                              <span className={"cicd-mark" + (cicdReady ? "" : " is-off")} title={cicdTitle} aria-label={cicdTitle}>
                                CI/CD
                              </span>
                            )}
                            {insts.length > 0 && <span className="cnt">{insts.length}</span>}
                          </button>
                          <div className="iio-sb2-spbody"><div className="iio-sb2-instwrap">
                            {insts.map(inst => (
                              <div key={inst.id} className="iio-sb2-inst">
                                <button type="button" className="iio-sb2-instrow" title={inst.name}
                                  onClick={() => { navigate("space", sp, inst); setMobileNavOpen(false); }}>
                                  <span className="led ok"/>
                                  <span className="lbl">{inst.name}</span>
                                </button>
                              </div>
                            ))}
                          </div></div>
                        </div>
                      );
                    })}
                  </div>
                </div></div>
              </div>

              {/* Transport (and other workspace entries) */}
              {workNavItems.map(n => (
                <button key={n.key} type="button" className={"iio-sb2-item" + (n.active ? " is-active" : "")} title={n.label} onClick={() => { n.onClick(); setMobileNavOpen(false); }}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

            {/* Administration: visible to admins only */}
            {isAdmin && (
              <div className="iio-sb2-sec">
                <div className="iio-sb2-label">{t("gruppe.verwaltung")}</div>
                {adminNavItems.map(n => (
                  <button key={n.key} type="button" className={"iio-sb2-item" + (n.active ? " is-active" : "")} title={n.label} onClick={() => { n.onClick(); setMobileNavOpen(false); }}>
                    <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                  </button>
                ))}
              </div>
            )}

          </nav>
            <div className={"iio-sb2-fade top" + (sbFades.top ? " show" : "")} aria-hidden="true"/>
            <div className={"iio-sb2-fade bot" + (sbFades.bot ? " show" : "")} aria-hidden="true"/>
          </div>

          {/* Zone 3: user */}
          <div className={"iio-sb2-foot" + (sbUserMenu ? " open" : "")}>
            <button type="button" className="iio-sb2-user" title={user.displayName || user.username}
              onClick={(e) => { e.stopPropagation(); setSbUserMenu(o => !o); }}>
              <span className="ava">{user.displayName?.[0]?.toUpperCase() || "?"}</span>
              <span className="uinfo">
                <span className="uname">{user.displayName || user.username}</span>
                <span className="urole">{user.role === "admin" ? t("benutzer.rolleAdmin") : t("benutzer.rolleBenutzer")}</span>
              </span>
              <svg className="uchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            {sbUserMenu && (
              <div className="iio-sb2-umenu" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => { setSbUserMenu(false); setShowSettings(true); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  {t("benutzer.einstellungen")}
                </button>
                <button type="button" onClick={() => { toggleTheme(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
                  {t("benutzer.themaWechseln")}
                </button>
                <div className="sep"/>
                <button type="button" className="danger" onClick={() => { setSbUserMenu(false); logout(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/></svg>
                  {t("benutzer.abmelden")}
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Topbar, running across the full width */}
        <header className="iio-topbar">
          {/* Brand plus sidebar toggle. Both toggles mean the same target (show
              the navigation or take it away) and share the same place; only one
              of them is visible at a time, depending on the width. */}
          <div className="iio-topbar-lead">
            <button type="button" className="iio-topbar-mobile-toggle" onClick={() => setMobileNavOpen(o => !o)} aria-label={t("seitenleiste.menue")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <button type="button" className="iio-topbar-rail-toggle" onClick={toggleSbRail} aria-pressed={sbRail} title={sbRail ? t("seitenleiste.ausklappen") : t("seitenleiste.einklappen")} aria-label={sbRail ? t("seitenleiste.ausklappen") : t("seitenleiste.einklappen")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/></svg>
            </button>
            <button type="button" className="iio-topbar-brand" onClick={() => { navigate("spaces", null, null); loadSpaces(); setMobileNavOpen(false); }} title={branding.title || "abat CI/CD GUI"}>
              <span className="iio-topbar-logo">
                {branding.logoUrl
                  ? <img src={branding.logoUrl} alt=""/>
                  : <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 26a6 6 0 0 1-.9-11.93A8.5 8.5 0 0 1 26.6 12.3 6.5 6.5 0 0 1 30 24.8"/>
                      <path d="M13 30h14"/><path d="M13 30l3-3M13 30l3 3"/><path d="M27 30l-3-3M27 30l-3 3" opacity=".45"/>
                      <circle cx="20" cy="19" r="1.4" fill="currentColor" stroke="none"/><path d="M20 20.5V26" opacity=".6"/>
                    </svg>}
              </span>
              <span className="iio-topbar-wm">
                <span className="w1">{branding.title || "abat CI/CD GUI"}</span>
                {/* The subtitle comes from the branding settings. A fixed string
                    stood here, so the configured value was never shown. If it is
                    empty the line is dropped entirely, instead of producing 3px
                    of margin and an empty line height. */}
                {branding.subtitle && <span className="w2">{branding.subtitle}</span>}
              </span>
            </button>
          </div>

          <div className="iio-topbar-search" style={{ position: "relative" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={searchQuery} onChange={e => handleSearch(e.target.value)} placeholder={t("suche.platzhalterSpacesInstanzen")}/>
            {searchResults && searchQuery && (
              <div className="iio-search-results">
                {searchResults.spaces?.length > 0 && <>
                  <div className="iio-search-section">{t("suche.spaces")}</div>
                  {searchResults.spaces.map(sp => (
                    <button key={sp.id} className="iio-search-item" onClick={() => { setSearchQuery(""); setSearchResults(null); navigate("space", sp, null); }}>
                      <span>{sp.name}</span><span className="iio-search-meta">{t("suche.typSpace")}</span>
                    </button>
                  ))}
                </>}
                {searchResults.instances?.length > 0 && <>
                  <div className="iio-search-section">{t("suche.instanzen")}</div>
                  {searchResults.instances.map(i => (
                    <button key={i.id} className="iio-search-item" onClick={() => { setSearchQuery(""); setSearchResults(null); const sp = spaces.find(s => s.id === i.spaceId); navigate("space", sp, i); }}>
                      <span>{i.name}</span><span className="iio-search-meta">{i.spaceName}</span>
                    </button>
                  ))}
                </>}
                {!searchResults.spaces?.length && !searchResults.instances?.length && (
                  <div className="iio-search-empty">{t("suche.keineErgebnisse")}</div>
                )}
              </div>
            )}
          </div>

          <div className="iio-topbar-right">
            <button type="button" className="iio-icon-btn" onClick={toggleTheme} title={t("benutzer.themaWechseln")}>
              {theme === "dark"
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
            </button>
            <button type="button" className="iio-icon-btn" onClick={logout} title={t("benutzer.abmelden")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </header>

        {/* Main content */}
        <main className={"iio-main" + (page === "transport-v2" ? " iio-main--full" : "")}>
          {showSettings && <SettingsModal onClose={() => setShowSettings(false)} user={user} onUpdate={u => setUser(prev => ({ ...prev, ...u }))} onBrandingUpdate={setBranding}/>}
          {page === "spaces" && <SpacesHome spaces={spaces} cicdStatus={cicdStatus} onRefresh={loadSpaces} onOpenSpace={(s) => navigate("space", s, null)}/>}
          {page === "space" && activeSpace && <SpaceDetail space={activeSpace} cicdStatus={cicdStatus} onBack={() => { navigate("spaces", null, null); loadSpaces(); }} onSpaceUpdated={updated => { setActiveSpace(s => ({ ...s, ...updated })); loadSpaces(); }}/>}
          {page === "space" && !activeSpace && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh", color: "var(--iio-text-3)" }}>{t("gemeinsam:zustand.laedt")}</div>}
          {page === "transport-v2" && <TransportV2Page spaces={spaces} user={user}/>}
          {page === "benutzer-anlegen" && <BenutzerAnlegen user={user} spaces={spaces} onDone={() => navigate("spaces", null, null)}/>}
          {page === "benutzer-verwalten" && <BenutzerVerwalten user={user} spaces={spaces}/>}
          {page === "cicd-setup" && <CicdSetup user={user} spaces={spaces}/>}
          {/* After unbinding a connection in the monitor, the marker on the spaces is
              stale. onCicdRefresh reloads it, otherwise it keeps showing the old state. */}
          {page === "cicd-pipeline" && <CicdPipeline user={user} spaces={spaces} onCicdRefresh={loadCicdStatus}/>}
          {page === "transport-freigabe" && <ReleaseFreigabe user={user} spaces={spaces}/>}
          {page === "invite" && inviteToken && <InviteAccept token={inviteToken} onDone={() => { navigate("spaces", null, null); loadSpaces(); }}/>}
        </main>

        {/* Footer */}
        <footer className="iio-footer">
          <span>abat AG · 2026</span>
          <span className="iio-sep">·</span>
          <span>{branding.title || "abat CI/CD GUI"}</span>
        </footer>

      </div>
    </div>
    </ThemeCtx.Provider>
    </ThemeProvider>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}
